import { useMemo } from "react";
import useRemediationItemsSplit from "../General/useRemediationItemsSplit";
import type { SelectableFinding } from "../types/FindingsContext";

export const FINDINGS_PATH = "/findings";
export const REMEDIATION_ITEMS_PATH = "/remediation-items";
export const SEEMPLICITY_FINDINGS_PATH = "/seemplicity-findings";

export const buildSeemplicityFindingHref = (id?: string): string =>
  id === undefined
    ? SEEMPLICITY_FINDINGS_PATH
    : `${SEEMPLICITY_FINDINGS_PATH}/${id}`;

export const buildLegacyMemberFindingHref = (id: string): string =>
  `${FINDINGS_PATH}/${id}/member`;

interface UseFindingEntityReturnValue {
  buildRemediationItemHref: (id?: string) => string;
  buildMemberFindingHref: (id: string) => string;
  entityLabel: string;
  entityLabelLowercase: string;
  backLabel: string;
}

export const useFindingEntity = (): UseFindingEntityReturnValue => {
  const isRemediationItemsSplit = useRemediationItemsSplit();

  return useMemo(() => {
    const remediationItemsPath = isRemediationItemsSplit
      ? REMEDIATION_ITEMS_PATH
      : FINDINGS_PATH;

    const entityLabel = isRemediationItemsSplit
      ? "Remediation Items"
      : "Findings";

    return {
      buildRemediationItemHref: (id?: string) =>
        id === undefined
          ? remediationItemsPath
          : `${remediationItemsPath}/${id}`,
      buildMemberFindingHref: (id: string) =>
        isRemediationItemsSplit
          ? buildSeemplicityFindingHref(id)
          : buildLegacyMemberFindingHref(id),
      entityLabel,
      entityLabelLowercase: isRemediationItemsSplit
        ? "remediation items"
        : "findings",
      backLabel: `Back To ${entityLabel}`,
    };
  }, [isRemediationItemsSplit]);
};

interface UseRemediationItemHrefsReturnValue {
  buildRowHref: (finding: SelectableFinding) => string;
  buildListHref: () => string;
}

export const useRemediationItemHrefs =
  (): UseRemediationItemHrefsReturnValue => {
    const { buildRemediationItemHref } = useFindingEntity();

    return useMemo(
      () => ({
        buildRowHref: (finding: SelectableFinding) =>
          buildRemediationItemHref(btoa(`Finding:${finding.id_int}`)),
        buildListHref: () => buildRemediationItemHref(),
      }),
      [buildRemediationItemHref]
    );
  };
